// ─────────────────────────────────────────────────────────────────────────────
// General-purpose helpers
// ─────────────────────────────────────────────────────────────────────────────

import { APP_BASE } from '../config/app';

/** Format an ISO date string to a human-readable format. */
export function formatDate(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/** Short date format e.g. "Sat 15 Mar" */
export function formatDateShort(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Ordinal suffix e.g. 1st, 2nd, 3rd */
export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

/** Debounce a function call. */
export function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

/** Clamp a number between min and max. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Stableford points label for display. */
export function pointsLabel(points: number | null): string {
  if (points === null) return '–';
  return points.toString();
}

/** Net score label (+1 = bogey, -1 = birdie, etc.) */
export function netScoreLabel(netVsPar: number): string {
  if (netVsPar > 0) return `+${netVsPar}`;
  if (netVsPar < 0) return `${netVsPar}`;
  return 'E';
}

/** Score descriptor name */
export function scoreDescriptor(netVsPar: number): string {
  if (netVsPar <= -3) return 'Albatross';
  if (netVsPar === -2) return 'Eagle';
  if (netVsPar === -1) return 'Birdie';
  if (netVsPar === 0) return 'Par';
  if (netVsPar === 1) return 'Bogey';
  if (netVsPar === 2) return 'Double';
  return `+${netVsPar}`;
}

export function roundScoringUrl(roundId: string): string {
  return `${window.location.origin}${APP_BASE}/rounds/${roundId}/group?create=1`;
}

export function groupJoinUrl(roundId: string, joinCode: string): string {
  return `${window.location.origin}${APP_BASE}/rounds/${roundId}/join/${joinCode}`;
}

export function tournamentUrl(): string {
  return `${window.location.origin}${APP_BASE}`;
}

/** Simple class merging (no clsx dependency needed for small usage) */
export function cx(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Truncate a string with ellipsis */
export function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? str.slice(0, maxLen - 1) + '…' : str;
}

/** Generate a placeholder group name if AI generation fails */
export function fallbackGroupName(playerNames: string[]): string {
  const last = playerNames.map((n) => n.split(' ').pop() ?? n);
  return last.join(' & ');
}
